/**
 * Synchronous main-thread cost of one completion-provider invocation — the
 * `prov_*` buckets of `completion.session_summary` (1.5.1).
 *
 * Elapsed wall time from `startProviderCost()` to `finish()` equals
 * main-thread time only while the whole request runs inside ONE task: the
 * resolvers await already-settled promises, which continue as microtasks of
 * the same task. A request that awaits real I/O (MSSQL's cold column fetch,
 * the procedural-parse worker, a lazily loaded grammar, the mock LSP) spans
 * several tasks, and elapsed time would count whatever ran in between as
 * provider work — so such a run is reported as unmeasurable (`undefined`).
 *
 * Detection is structural, not timing-based: every completion-path await
 * that waits on real I/O calls {@link noteCompletionIoWait} before awaiting,
 * and a run during which any note happened is unmeasurable. (A zero-delay
 * probe timer alone cannot see a reply that was queued BEFORE the run
 * started: the event loop runs that reply — and anything queued ahead of it —
 * before the probe, and the run can finish inside the reply's task.) The
 * probe stays as a backstop for an unnoted await that crosses a task
 * boundary with nothing queued ahead of it. A note from a concurrent run also
 * marks this run unmeasurable — conservative: fewer samples, never inflated.
 *
 * Contract for new code: an `await` on the completion path that can wait on
 * IPC, a worker, a dynamic `import()` or a timer must call
 * `noteCompletionIoWait()` first — only when it really waits: a cache hit or
 * an immediate return stays measurable. Lazily loaded modules go through
 * {@link loadCompletionModule}, which notes only while a module is still
 * loading (`providerCostIoSites.test.ts` pins the current sites). Known gap:
 * a grammar module's own first-use parser-class load (`loadCtors`, outside
 * this feature) is noted only when it coincides with the first load of that
 * module through `loadCompletionModule` — the normal case.
 */

export type ProviderCostTimer = {
  /** Elapsed ms, or `undefined` when the request waited on real I/O / crossed a task boundary. */
  readonly finish: () => number | undefined;
};

let ioWaitEpoch = 0;

/** A completion-path await is about to wait on real I/O — see the module doc. */
export function noteCompletionIoWait(): void {
  ioWaitEpoch += 1;
}

/** Test seam: how many real-I/O waits have been noted so far. */
export function __completionIoWaitCountForTest(): number {
  return ioWaitEpoch;
}

type LazyModule = { readonly promise: Promise<unknown>; settled: boolean };
const lazyModules = new Map<string, LazyModule>();

/**
 * Await a lazily imported module on the completion path. The first request
 * (and any request while the load is still in flight) waits on real I/O and
 * notes it; once the module has loaded, later requests get the settled
 * promise, which continues as a microtask of the same task — measurable. A
 * failed load is forgotten so the next request retries (and notes again).
 * `key` must identify the module (use its import specifier).
 */
export function loadCompletionModule<T>(key: string, load: () => Promise<T>): Promise<T> {
  let entry = lazyModules.get(key);
  if (!entry) {
    const promise = load();
    const created: LazyModule = { promise, settled: false };
    promise.then(
      () => {
        created.settled = true;
      },
      () => {
        if (lazyModules.get(key) === created) lazyModules.delete(key);
      },
    );
    lazyModules.set(key, created);
    entry = created;
  }
  if (!entry.settled) noteCompletionIoWait();
  return entry.promise as Promise<T>;
}

export function startProviderCost(): ProviderCostTimer {
  const startedAt = performance.now();
  const epochAtStart = ioWaitEpoch;
  let crossedTaskBoundary = false;
  const probe = setTimeout(() => {
    crossedTaskBoundary = true;
  }, 0);
  return {
    finish: () => {
      clearTimeout(probe);
      if (crossedTaskBoundary || ioWaitEpoch !== epochAtStart) return undefined;
      return performance.now() - startedAt;
    },
  };
}
